import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import api from '../services/api';
import { formatHistoryDateTime } from '../utils/dateUtils';

interface NotificationEmailDetail {
  recno: number;
  datetime: string | null;
  createdby: string;
  type: string;
  emailfrom: string;
  emailto: string;
  subject: string;
  body: string;
}

interface NotificationEmailModalProps {
  /** notificationssent recno to load; null = modal closed (renders nothing). */
  recno: number | null;
  onClose: () => void;
}

const LabeledRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex text-sm">
    <div className="w-20 flex-shrink-0 font-medium text-muted">{label}</div>
    <div className="flex-1 min-w-0 text-body break-words">{value || '-'}</div>
  </div>
);

export const NotificationEmailModal: React.FC<NotificationEmailModalProps> = ({
  recno,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<NotificationEmailDetail | null>(null);

  useEffect(() => {
    if (recno == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    api
      .get<NotificationEmailDetail>(`/orders/notification-email/${recno}`)
      .then((response) => {
        if (!cancelled) setDetail(response.data);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Failed to load email notification');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recno]);

  useEffect(() => {
    if (recno == null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Capture-phase + stopPropagation so ProDetailSlideOver's bubble-phase
      // window Escape handler doesn't also fire and close the whole drawer.
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [recno, onClose]);

  // Read-only email preview: Alt+C closes. Must sit ABOVE the `recno == null`
  // guard — this modal stays mounted while closed, so a hook below the guard
  // would change the hook count when a recno arrives (React #310).
  // `layer: recno != null` is what makes it inert while closed.
  useAltHotkeys({ C: onClose }, undefined, { layer: recno != null });

  // Stays mounted while closed, so the drag offset must reset via `active`.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(recno != null);

  if (recno == null) return null;

  const body = detail?.body || '';
  const isHtmlBody = /<html[\s>]/i.test(body);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/50"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={detail?.type ? `Notification - ${detail.type}` : 'Email Notification'}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 flex flex-col">
          {loading && (
            <p className="text-sm text-muted">Loading email...</p>
          )}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          {!loading && detail && (
            <>
              <div className="space-y-1.5">
                <LabeledRow label="From" value={detail.emailfrom} />
                <LabeledRow label="To" value={detail.emailto} />
                <LabeledRow label="Date" value={formatHistoryDateTime(detail.datetime)} />
                <LabeledRow label="Subject" value={detail.subject} />
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 my-4" />
              {isHtmlBody ? (
                <iframe
                  sandbox=""
                  srcDoc={body}
                  className="w-full flex-1 min-h-[400px] bg-white"
                  title="Email body"
                />
              ) : (
                <div className="whitespace-pre-wrap break-words text-sm text-body">
                  {body || '-'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-label bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationEmailModal;
